import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { theme } from '../../theme';
import { useVenueDetail } from '../../hooks/venue/useVenueDetail';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../utils/apiUtils';
import VenuePhotoGallery from '../../components/venue/shared/VenuePhotoGallery';
import VenueAmenityBadge from '../../components/venue/shared/VenueAmenityBadge';
import VenueReviewCard from '../../components/venue/reviews/VenueReviewCard';
import CreateVenueReviewModal from '../../components/venue/reviews/CreateVenueReviewModal';
import { CreateMatchFromBookingModal } from '../../components/modals/CreateMatchFromBookingModal';
import { HoldTimer } from '../../components/booking/HoldTimer';
import { ExpiredHoldModal } from '../../components/booking/ExpiredHoldModal';
import {
  MapPin,
  Users,
  Star,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  formatVenueType,
  formatRating,
  getVenueAmenities,
} from '../../utils/venueHelpers';
import type { VenueAvailability } from '../../types/venue';

const WEEKDAY_LABELS = ['LUN', 'MAR', 'MIE', 'JUE', 'VIE', 'SAB', 'DOM'];
const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const MONTH_SHORT = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

function formatDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function getWeekDays(dateStr: string): Date[] {
  const d = new Date(dateStr + 'T12:00:00');
  const dayOfWeek = d.getDay();
  // Monday-based: Monday=0, Sunday=6
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(d);
  monday.setDate(d.getDate() + mondayOffset);
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    days.push(day);
  }
  return days;
}

const VenueDetailPage: React.FC = () => {
  const { id, complexSlug, venueSlug } = useParams<{ id?: string; complexSlug?: string; venueSlug?: string }>();
  const navigate = useNavigate();
  const isSlugRoute = !!complexSlug && !!venueSlug;
  const { venue, loading, error, fetchVenue } = useVenueDetail(id || '', isSlugRoute ? `${complexSlug}/${venueSlug}` : undefined);
  const { user } = useAuth();
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  // Venue availability state
  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));
  const [venueAvailability, setVenueAvailability] = useState<VenueAvailability[]>([]);
  const [venueBookings, setVenueBookings] = useState<{ startTime: string; endTime: string; status: string }[]>([]);
  const [availLoading, setAvailLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<{
    venueId: string;
    date: string;
    startTime: string;
    endTime: string;
    price: number; // Monto a pagar (puede ser total o seña)
    fullTotal: number; // Precio total de la cancha
    paymentType: 'full' | 'deposit';
    isPeakHour: boolean;
  } | null>(null);

  // Booking state
  const [submitting, setSubmitting] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [createdBookingId, setCreatedBookingId] = useState<string | null>(null);
  const [showCreateMatch, setShowCreateMatch] = useState(false);

  // Hold state
  const [activeHold, setActiveHold] = useState<{ id: string; expiresAt: string } | null>(null);
  const [showExpiredModal, setShowExpiredModal] = useState(false);

  useEffect(() => {
    if (id || isSlugRoute) fetchVenue();
  }, [id, isSlugRoute, fetchVenue]);

  const venueId = venue?.id || id;

  const fetchAvailability = useCallback(async (date: string) => {
    if (!venueId) return;
    setAvailLoading(true);
    try {
      const res = await apiRequest<{ availability: VenueAvailability[]; bookings: { startTime: string; endTime: string; status: string }[] }>(
        `/venues/${venueId}/availability?date=${date}`
      );
      if (res.data) {
        setVenueAvailability(res.data.availability || []);
        setVenueBookings(res.data.bookings || []);
      }
    } catch {
      // ignore
    } finally {
      setAvailLoading(false);
    }
  }, [venueId]);

  useEffect(() => {
    if (id) {
      fetchAvailability(selectedDate);
    }
  }, [id, selectedDate, fetchAvailability]);

  const goToDate = useCallback((newDate: string) => {
    setSelectedDate(newDate);
    setSelectedSlot(null);
  }, []);

  // Calendar
  const weekDays = useMemo(() => getWeekDays(selectedDate), [selectedDate]);
  const selectedDateObj = new Date(selectedDate + 'T12:00:00');

  const handleSelectDay = useCallback((d: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const max = new Date();
    max.setHours(0, 0, 0, 0);
    max.setDate(max.getDate() + 7);
    if (d >= today && d <= max) {
      goToDate(formatDate(d));
    }
  }, [goToDate]);

  const handlePrevWeek = useCallback(() => {
    const prevMonday = new Date(weekDays[0]);
    prevMonday.setDate(prevMonday.getDate() - 7);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (prevMonday >= today) {
      goToDate(formatDate(prevMonday));
    } else {
      goToDate(formatDate(today));
    }
  }, [weekDays, goToDate]);

  const maxDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 7);
    return d;
  }, []);

  const handleNextWeek = useCallback(() => {
    const d = new Date(weekDays[0]);
    d.setDate(d.getDate() + 7);
    if (d <= maxDate) {
      handleSelectDay(d);
    }
  }, [weekDays, handleSelectDay, maxDate]);

  // Build time slots from venue availability + bookings
  const timeSlots = useMemo(() => {
    if (!venueAvailability.length || !venue) return [];
    const dateObj = new Date(selectedDate + 'T12:00:00');
    const dayOfWeek = dateObj.getDay(); // 0=Sunday, 6=Saturday
    const daySlots = venueAvailability.filter(a => a.dayOfWeek === dayOfWeek);
    if (daySlots.length === 0) return [];

    // Check if selected date is today
    const now = new Date();
    const today = formatDate(now);
    const isToday = selectedDate === today;
    const currentHour = now.getHours();

    const slots: { hour: number; startTime: string; endTime: string; available: boolean; isPeakHour: boolean; isPast: boolean; isBooked: boolean }[] = [];
    for (const avail of daySlots) {
      const startH = parseInt(avail.startTime.split(':')[0]);
      const endH = parseInt(avail.endTime.split(':')[0]);
      for (let h = startH; h < endH; h++) {
        const st = `${String(h).padStart(2, '0')}:00`;
        const et = `${String(h + 1).padStart(2, '0')}:00`;

        // Check if booked
        const isBooked = venueBookings.some(b => {
          const bs = parseInt(b.startTime.split(':')[0]);
          const be = parseInt(b.endTime.split(':')[0]);
          return h >= bs && h < be;
        });

        // Check if hour has passed (only for today)
        const isPast = isToday && h <= currentHour;

        // Slot is available only if not booked AND not in the past
        slots.push({
          hour: h,
          startTime: st,
          endTime: et,
          available: !isBooked && !isPast,
          isPeakHour: avail.isPeakHour,
          isPast,
          isBooked
        });
      }
    }
    return slots;
  }, [venueAvailability, venueBookings, selectedDate, venue]);

  const handleSelectSlot = useCallback((slot: { hour: number; startTime: string; endTime: string; isPeakHour: boolean }) => {
    if (!venue) return;

    const rate = Number(slot.isPeakHour && venue.peakHourRate ? venue.peakHourRate : venue.hourlyRate);
    const fullTotal = rate; // Por 1 hora

    // Determinar qué monto cobrar: total o seña
    const paymentType = venue.requiresFullPayment ? 'full' : 'deposit';
    const depositAmount = Number(venue.depositAmount || 0);

    // Si pide seña pero depositAmount es 0, cobrar total
    const price = paymentType === 'deposit' && depositAmount > 0
      ? depositAmount
      : fullTotal;

    setSelectedSlot({
      venueId: venue.id,
      date: selectedDate,
      startTime: slot.startTime,
      endTime: slot.endTime,
      price,
      fullTotal,
      paymentType,
      isPeakHour: slot.isPeakHour,
    });
  }, [venue, selectedDate]);

  // Booking confirm - Creates a payment hold
  const handleConfirm = async () => {
    if (!user || !selectedSlot) return;
    setSubmitting(true);
    setBookingError(null);
    try {
      const slot = selectedSlot;
      // Step 1: Create hold
      const res = await apiRequest('/bookings', {
        method: 'POST',
        body: {
          venueId: slot.venueId,
          bookingDate: slot.date,
          startTime: slot.startTime,
          endTime: slot.endTime,
        },
      });

      if (res.data?.holdId) {
        // Hold created successfully
        setActiveHold({
          id: res.data.holdId,
          expiresAt: res.data.expiresAt,
        });
      } else {
        setBookingError(res.error || 'Error al crear la reserva temporal');
      }
    } catch {
      setBookingError('Error de conexión');
    } finally {
      setSubmitting(false);
    }
  };

  // Proceed to payment from active hold
  const handleProceedToPayment = async () => {
    if (!activeHold) return;
    setSubmitting(true);
    setBookingError(null);
    try {
      // eslint-disable-next-line no-console
      console.log('🔵 Iniciando pago para hold:', activeHold.id);
      const payRes = await apiRequest(`/payment-holds/${activeHold.id}/init-payment`, {
        method: 'POST',
      });
      // eslint-disable-next-line no-console
      console.log('🔵 Respuesta de init-payment:', payRes);
      // eslint-disable-next-line no-console
      console.log('🔵 initPoint:', payRes.data?.initPoint);

      if (payRes.data?.initPoint) {
        // eslint-disable-next-line no-console
        console.log('✅ Redirigiendo a MercadoPago:', payRes.data.initPoint);
        window.location.href = payRes.data.initPoint;
        return;
      }
      // eslint-disable-next-line no-console
      console.error('❌ No se recibió initPoint en la respuesta');
      setBookingError('No se pudo iniciar el pago.');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error al iniciar el pago:', error);
      setBookingError('Error al iniciar el pago');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle hold expiration
  const handleHoldExpire = () => {
    setShowExpiredModal(true);
    setActiveHold(null);
  };

  // Retry booking after expiration
  const handleRetryBooking = () => {
    setShowExpiredModal(false);
    setSelectedSlot(null);
    fetchAvailability(selectedDate);
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <p style={{ color: '#9CA3AF', fontSize: 16 }}>Cargando cancha...</p>
        </div>
      </div>
    );
  }

  if (error || !venue) {
    return (
      <div style={pageStyle}>
        <div style={{ display: 'flex', flexDirection: 'column' as const, alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 16 }}>
          <p style={{ color: '#EF4444', fontSize: 18, fontWeight: 600 }}>Error al cargar la cancha</p>
          <p style={{ color: '#9CA3AF' }}>{error || 'Cancha no encontrada'}</p>
          <button style={primaryBtnStyle} onClick={() => navigate('/venues')}>
            <ArrowLeft size={18} />
            Volver a búsqueda
          </button>
        </div>
      </div>
    );
  }

  const amenities = getVenueAmenities(venue);
  const complexName = venue.complex?.name || venue.name;
  const slot = selectedSlot;
  const todayStr = formatDate(new Date());
  const maxDateStr = formatDate(maxDate);
  const canGoPrevWeek = todayStr < formatDate(weekDays[0]);
  const canGoNextWeek = formatDate(weekDays[6]) < maxDateStr;

  return (
    <div style={pageStyle}>
      {/* Back Button */}
      <div style={{ padding: '24px 48px 0' }}>
        <button
          style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', color: '#9CA3AF', fontSize: 14, cursor: 'pointer', padding: 0 }}
          onClick={() => navigate('/venues')}
        >
          <ArrowLeft size={18} color="#9CA3AF" />
          Volver a canchas
        </button>
      </div>

      {/* Content Area */}
      <div style={contentAreaStyle}>
        {/* Main Column */}
        <div style={mainColumnStyle}>
          {/* Photo Gallery */}
          <VenuePhotoGallery photos={venue.photos || []} venueName={venue.name} />

          {/* Info Section */}
          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 20 }}>
            {/* Title Row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8 }}>
                <h1 style={venueTitleStyle}>{complexName}</h1>
                <p style={{ fontSize: 14, color: '#9CA3AF', margin: 0 }}>
                  {formatVenueType(venue.venueType)} · {venue.surfaceType}
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' as const, alignItems: 'flex-end', gap: 8 }}>
                {venue.averageRating && (
                  <div style={ratingBadgeStyle}>
                    <Star size={16} fill="#00E676" color="#00E676" />
                    <span>{formatRating(venue.averageRating)}</span>
                  </div>
                )}
                {venue.reviewCount != null && venue.reviewCount > 0 && (
                  <span style={{ color: '#9CA3AF', fontSize: 13 }}>({venue.reviewCount} reviews)</span>
                )}
                <span style={{ color: '#9CA3AF', fontSize: 14 }}>Capacidad: {venue.capacity} jugadores</span>
              </div>
            </div>

            {/* Location Section */}
            <div style={locationCardStyle}>
              <MapPin size={22} color="#00E676" style={{ flexShrink: 0, marginTop: 2 }} />
              <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 4 }}>
                <p style={{ color: '#00E676', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, margin: 0, letterSpacing: 0.5 }}>UBICACIÓN</p>
                <p style={{ color: '#FFFFFF', fontSize: 14, margin: 0 }}>{venue.address}</p>
                <p style={{ color: '#9CA3AF', fontSize: 13, margin: 0 }}>{venue.city}, {venue.province}</p>
              </div>
            </div>

            {/* Description */}
            {venue.description && (
              <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 12 }}>
                <h2 style={sectionTitleStyle}>DESCRIPCIÓN</h2>
                <p style={{ color: '#FFFFFF', fontSize: 14, lineHeight: 1.6, margin: 0 }}>{venue.description}</p>
              </div>
            )}
          </div>

          {/* Amenities */}
          {amenities.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 16 }}>
              <h2 style={sectionTitleStyle}>AMENITIES</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                {amenities.map((amenity, index) => (
                  <VenueAmenityBadge key={index} amenity={amenity} size="medium" />
                ))}
              </div>
            </div>
          )}

          {/* Reviews */}
          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={sectionTitleStyle}>REVIEWS</h2>
              {user && (
                <button style={reviewBtnStyle} onClick={() => setIsReviewModalOpen(true)}>
                  Escribir Review
                </button>
              )}
            </div>

            {venue.reviews && venue.reviews.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 20 }}>
                {venue.reviews.map((review) => (
                  <VenueReviewCard key={review.id} review={review} />
                ))}
              </div>
            ) : (
              <div style={emptyReviewsStyle}>
                <p>Aún no hay reseñas para esta cancha</p>
                <p style={{ fontSize: 13, marginTop: 8, color: '#6B7280' }}>Sé el primero en dejar una reseña</p>
              </div>
            )}
          </div>
        </div>

        {/* Side Column - Booking Card */}
        <div style={{ width: 450, flexShrink: 0 }}>
          <div style={bookingCardStyle}>
            <h2 style={{ color: '#00E676', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 22, letterSpacing: 0.5, margin: 0 }}>
              RESERVAR CANCHA
            </h2>

            {/* Date Section */}
            <>
              <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 12 }}>
                <p style={labelStyle}>Seleccionar Fecha</p>

                  {/* Calendar */}
                  <div style={calendarStyle}>
                    {/* Calendar Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 4px' }}>
                      <button style={calNavBtnStyle} onClick={handlePrevWeek} disabled={!canGoPrevWeek}>
                        <ChevronLeft size={18} color={canGoPrevWeek ? '#00E676' : '#555'} />
                      </button>
                      <span style={{ color: '#FFFFFF', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 18 }}>
                        {MONTH_NAMES[selectedDateObj.getMonth()]} {selectedDateObj.getFullYear()}
                      </span>
                      <button style={calNavBtnStyle} onClick={handleNextWeek} disabled={!canGoNextWeek}>
                        <ChevronRight size={18} color={canGoNextWeek ? '#00E676' : '#555'} />
                      </button>
                    </div>

                    {/* Weekday Labels */}
                    <div style={{ display: 'flex', gap: 8 }}>
                      {WEEKDAY_LABELS.map(wd => (
                        <div key={wd} style={{ flex: 1, textAlign: 'center' as const }}>
                          <span style={{ color: '#6C757D', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 11, fontWeight: 600 }}>{wd}</span>
                        </div>
                      ))}
                    </div>

                    {/* Day Numbers */}
                    <div style={{ display: 'flex', gap: 8 }}>
                      {weekDays.map((day) => {
                        const dayStr = formatDate(day);
                        const isSelected = dayStr === selectedDate;
                        const isPast = dayStr < todayStr;
                        const isBeyondLimit = dayStr > maxDateStr;
                        const isDisabled = isPast || isBeyondLimit;
                        return (
                          <div
                            key={dayStr}
                            onClick={() => !isDisabled && handleSelectDay(day)}
                            style={{
                              flex: 1,
                              height: 40,
                              borderRadius: 10,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: isDisabled ? 'default' : 'pointer',
                              backgroundColor: isSelected ? '#00E676' : '#23272A',
                              border: isSelected ? 'none' : '1px solid #33383B',
                              opacity: isDisabled ? 0.4 : 1,
                            }}
                          >
                            <span style={{
                              color: isSelected ? '#181A1B' : '#B0BEC5',
                              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                              fontSize: 14,
                              fontWeight: isSelected ? 700 : 500,
                            }}>
                              {day.getDate()}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Time Slots */}
                <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 12 }}>
                  <p style={labelStyle}>Horarios Disponibles</p>
                  <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8 }}>
                    {availLoading ? (
                      <p style={{ color: '#9CA3AF', fontSize: 14, textAlign: 'center' as const, padding: 20 }}>Cargando...</p>
                    ) : timeSlots.length === 0 ? (
                      <p style={{ color: '#9CA3AF', fontSize: 14, textAlign: 'center' as const, padding: 20 }}>No hay horarios disponibles</p>
                    ) : (
                      timeSlots.map(ts => {
                        const isSelected = slot?.startTime === ts.startTime && slot?.date === selectedDate;
                        // Determine status message
                        let statusLabel = '';
                        if (!ts.available) {
                          if (ts.isPast) {
                            statusLabel = 'PASADO';
                          } else if (ts.isBooked) {
                            statusLabel = 'OCUPADO';
                          }
                        }

                        return (
                          <div
                            key={ts.hour}
                            onClick={() => ts.available && handleSelectSlot(ts)}
                            style={{
                              height: 48,
                              borderRadius: 10,
                              display: 'flex',
                              flexDirection: 'column' as const,
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: ts.available ? 'pointer' : 'default',
                              backgroundColor: isSelected ? '#00E676' : ts.available ? '#23272A' : '#1A1C1D',
                              border: isSelected ? 'none' : '1px solid #33383B',
                              opacity: ts.available ? 1 : 0.4,
                              gap: 4,
                            }}
                          >
                            <span style={{
                              color: isSelected ? '#181A1B' : ts.available ? '#FFFFFF' : '#666',
                              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                              fontSize: 14,
                              fontWeight: isSelected ? 700 : 600,
                            }}>
                              {ts.startTime} - {ts.endTime}
                            </span>
                            {statusLabel && (
                              <span style={{ color: '#666', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 9, fontWeight: 700 }}>
                                {statusLabel}
                              </span>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Summary */}
                {slot && (
                  <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 12 }}>
                    <p style={labelStyle}>Resumen</p>
                    <div style={summaryCardStyle}>
                      <div style={summaryRowStyle}>
                        <span style={{ color: '#B0BEC5', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 13 }}>Fecha</span>
                        <span style={{ color: '#FFFFFF', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 13, fontWeight: 600 }}>
                          {DAY_NAMES[new Date(slot.date + 'T12:00:00').getDay()]} {new Date(slot.date + 'T12:00:00').getDate()} {MONTH_SHORT[new Date(slot.date + 'T12:00:00').getMonth()]}
                        </span>
                      </div>
                      <div style={summaryRowStyle}>
                        <span style={{ color: '#B0BEC5', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 13 }}>Horario</span>
                        <span style={{ color: '#FFFFFF', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 13, fontWeight: 600 }}>
                          {slot.startTime} - {slot.endTime}
                        </span>
                      </div>
                      <div style={{ height: 1, backgroundColor: '#33383B', width: '100%' }} />

                      {/* Precio total de la cancha */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#B0BEC5', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 13 }}>Precio cancha</span>
                        <span style={{ color: '#FFFFFF', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 13, fontWeight: 600 }}>
                          ${slot.fullTotal.toLocaleString('es-AR')}
                        </span>
                      </div>

                      {/* Monto a pagar (puede ser seña o total) */}
                      {slot.paymentType === 'deposit' && (
                        <>
                          <div style={{ height: 1, backgroundColor: '#33383B', width: '100%' }} />
                          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 4 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ color: '#00E676', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 16 }}>SEÑA A PAGAR</span>
                              <span style={{ color: '#00E676', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 24 }}>
                                ${slot.price.toLocaleString('es-AR')}
                              </span>
                            </div>
                            <span style={{ color: '#9CA3AF', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 11, textAlign: 'right' as const }}>
                              (Resto: ${(slot.fullTotal - slot.price).toLocaleString('es-AR')})
                            </span>
                          </div>
                        </>
                      )}

                      {slot.paymentType === 'full' && (
                        <>
                          <div style={{ height: 1, backgroundColor: '#33383B', width: '100%' }} />
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: '#00E676', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 16 }}>TOTAL A PAGAR</span>
                            <span style={{ color: '#00E676', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 24 }}>
                              ${slot.price.toLocaleString('es-AR')}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Hold Timer */}
                {activeHold && (
                  <HoldTimer
                    expiresAt={activeHold.expiresAt}
                    onExpire={handleHoldExpire}
                  />
                )}

                {/* Error */}
                {bookingError && (
                  <div style={{ padding: 10, borderRadius: 8, backgroundColor: 'rgba(255,23,68,0.1)', color: '#EF4444', fontSize: 13 }}>
                    {bookingError}
                  </div>
                )}

                {/* Confirm Button / Payment Button */}
                {slot && !createdBookingId && (
                  user ? (
                    activeHold ? (
                      // Show "Proceed to Payment" when hold is active
                      <button
                        onClick={handleProceedToPayment}
                        disabled={submitting}
                        style={{
                          ...confirmBtnStyle,
                          opacity: submitting ? 0.7 : 1,
                          cursor: submitting ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <span style={{ color: '#181A1B', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 18, letterSpacing: 0.5 }}>
                          {submitting ? 'PROCESANDO...' : 'PROCEDER AL PAGO'}
                        </span>
                      </button>
                    ) : (
                      // Show "Confirm Booking" when no hold yet
                      <button
                        onClick={handleConfirm}
                        disabled={submitting}
                        style={{
                          ...confirmBtnStyle,
                          opacity: submitting ? 0.7 : 1,
                          cursor: submitting ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <span style={{ color: '#181A1B', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 18, letterSpacing: 0.5 }}>
                          {submitting ? 'PROCESANDO...' : 'CONFIRMAR RESERVA'}
                        </span>
                      </button>
                    )
                  ) : (
                    <div style={{ textAlign: 'center' as const, padding: 14, borderRadius: 12, backgroundColor: 'rgba(0,230,118,0.1)', color: '#00E676', fontSize: 14, fontWeight: 600 }}>
                      Iniciá sesión para reservar
                    </div>
                  )
                )}

                {/* Post-booking actions */}
                {createdBookingId && !showCreateMatch && (
                  <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 10 }}>
                    <button onClick={() => setShowCreateMatch(true)} style={confirmBtnStyle}>
                      <span style={{ color: '#181A1B', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 16 }}>Crear Partido con esta reserva</span>
                    </button>
                    <button
                      onClick={() => navigate('/my-bookings')}
                      style={{ ...confirmBtnStyle, backgroundColor: 'transparent', border: '1px solid #33383B' }}
                    >
                      <span style={{ color: '#9CA3AF', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 16 }}>Ir a mis reservas</span>
                    </button>
                  </div>
                )}
            </>
          </div>
        </div>
      </div>

      {/* Review Modal */}
      {isReviewModalOpen && (
        <CreateVenueReviewModal
          venueName={venue.name}
          venueId={venue.id}
          onClose={() => setIsReviewModalOpen(false)}
          onSubmit={async (reviewData) => {
            const response = await apiRequest('/venue-reviews', {
              method: 'POST',
              body: { ...reviewData, venueId: venue.id },
            });
            if (response.data) {
              await fetchVenue();
            } else {
              throw new Error(response.error || 'Error al crear review');
            }
          }}
        />
      )}

      {/* Create Match Modal */}
      {showCreateMatch && createdBookingId && (
        <CreateMatchFromBookingModal
          bookingId={createdBookingId}
          onClose={() => setShowCreateMatch(false)}
          onSuccess={(matchId: string) => {
            setShowCreateMatch(false);
            navigate(`/match/${matchId}`);
          }}
        />
      )}

      {/* Expired Hold Modal */}
      <ExpiredHoldModal
        isOpen={showExpiredModal}
        onRetry={handleRetryBooking}
        onClose={() => {
          setShowExpiredModal(false);
          navigate('/venues');
        }}
      />
    </div>
  );
};

// ============ STYLES ============

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  backgroundColor: '#181A1B',
};


const contentAreaStyle: React.CSSProperties = {
  display: 'flex',
  gap: 32,
  padding: '32px 48px',
};

const mainColumnStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 28,
};

const venueTitleStyle: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 700,
  color: '#FFFFFF',
  fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
  letterSpacing: 0.5,
  margin: 0,
};

const ratingBadgeStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  backgroundColor: '#00E676',
  color: '#000',
  padding: '6px 12px',
  borderRadius: 20,
  fontSize: 14,
  fontWeight: 600,
};

const locationCardStyle: React.CSSProperties = {
  display: 'flex',
  gap: 12,
  padding: 20,
  backgroundColor: '#23272A',
  borderRadius: 12,
  border: '1px solid #33383B',
};

const sectionTitleStyle: React.CSSProperties = {
  color: '#00E676',
  fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
  fontSize: 18,
  letterSpacing: 0.5,
  margin: 0,
};

const reviewBtnStyle: React.CSSProperties = {
  padding: '10px 20px',
  borderRadius: 10,
  backgroundColor: 'transparent',
  border: '1px solid #00E676',
  color: '#00E676',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const emptyReviewsStyle: React.CSSProperties = {
  padding: 40,
  backgroundColor: '#23272A',
  borderRadius: 12,
  border: '1px solid #33383B',
  textAlign: 'center',
  color: '#9CA3AF',
};

// Booking Card
const bookingCardStyle: React.CSSProperties = {
  backgroundColor: '#23272A',
  borderRadius: 18,
  padding: 24,
  border: '1px solid #33383B',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.6)',
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
  position: 'sticky',
  top: 32,
};

const labelStyle: React.CSSProperties = {
  color: '#FFFFFF',
  fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
  fontSize: 16,
  margin: 0,
};

const calendarStyle: React.CSSProperties = {
  backgroundColor: '#181A1B',
  borderRadius: 12,
  padding: 20,
  border: '1px solid #33383B',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

const calNavBtnStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 8,
  backgroundColor: '#181A1B',
  border: '1px solid #33383B',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
  color: '#00E676',
};

const summaryCardStyle: React.CSSProperties = {
  backgroundColor: '#181A1B',
  borderRadius: 12,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const summaryRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const confirmBtnStyle: React.CSSProperties = {
  width: '100%',
  height: 56,
  borderRadius: 12,
  backgroundColor: '#00E676',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  boxShadow: '0 4px 16px rgba(0, 230, 118, 0.25)',
};

const primaryBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '12px 24px',
  backgroundColor: '#00E676',
  color: '#000',
  border: 'none',
  borderRadius: 12,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

export default VenueDetailPage;
